import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND = { r: 11, g: 141, b: 149 };      // Teal utama
const BRAND_DARK = { r: 6, g: 95, b: 100 };   // Teal gelap
const GRAY_TEXT = { r: 60, g: 60, b: 60 };
const GRAY_MUTED = { r: 130, g: 130, b: 130 };
const GRAY_LINE = { r: 220, g: 224, b: 228 };

export function generatePDF({ title, subtitle, headers, rows, meta, filename, settings, format = "a4", orientation = "landscape" }) {
  const doc = new jsPDF({ orientation, unit: "mm", format });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const isA5 = format === "a5";
  const margin = isA5 ? 10 : 14;

  // === HEADER — 3-layer band (brand gradient effect) ===
  doc.setFillColor(BRAND_DARK.r, BRAND_DARK.g, BRAND_DARK.b);
  doc.rect(0, 0, W, isA5 ? 20 : 22, "F");
  // Aksen bar tipis di bawah
  doc.setFillColor(BRAND.r, BRAND.g, BRAND.b);
  doc.rect(0, isA5 ? 20 : 22, W, 1.5, "F");

  // Foundation name (kiri atas kecil)
  const fname = (settings?.foundation_short_name || settings?.foundation_name || "").toUpperCase();
  if (fname) {
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(isA5 ? 7 : 8);
    doc.text(fname, margin, isA5 ? 6 : 7);
  }

  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(isA5 ? 13 : 16);
  doc.text(title || "Laporan", margin, isA5 ? 14 : 16);

  // Subtitle (kanan)
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(isA5 ? 8 : 9.5);
    doc.text(subtitle, W - margin, isA5 ? 14 : 16, { align: "right" });
  }

  // === META CARD — grid 2 kolom modern ===
  let y = (isA5 ? 22 : 25) + 6;
  const metaEntries = [["Dicetak", new Date().toLocaleString("id-ID", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })]];
  if (meta) Object.entries(meta).forEach(([k, v]) => metaEntries.push([k, String(v)]));

  const cols = 3;
  const colW = (W - margin * 2) / cols;
  const rowH = 8;
  const metaRows = Math.ceil(metaEntries.length / cols);

  // Card background subtle
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(GRAY_LINE.r, GRAY_LINE.g, GRAY_LINE.b);
  doc.setLineWidth(0.2);
  doc.roundedRect(margin, y, W - margin * 2, metaRows * rowH + 3, 1.5, 1.5, "FD");

  metaEntries.forEach(([k, v], i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = margin + 3 + col * colW;
    const cy = y + 4 + row * rowH;
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(GRAY_MUTED.r, GRAY_MUTED.g, GRAY_MUTED.b);
    doc.text(String(k).toUpperCase(), cx, cy);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(GRAY_TEXT.r, GRAY_TEXT.g, GRAY_TEXT.b);
    doc.text(String(v), cx, cy + 4);
  });

  const tableStartY = y + metaRows * rowH + 8;

  // === TABLE modern ===
  autoTable(doc, {
    startY: tableStartY,
    head: [headers],
    body: rows,
    styles: { fontSize: isA5 ? 7.5 : 8.5, cellPadding: isA5 ? 2 : 3, lineColor: [230, 232, 236], lineWidth: 0.15 },
    headStyles: {
      fillColor: [BRAND.r, BRAND.g, BRAND.b],
      textColor: 255,
      fontStyle: "bold",
      fontSize: isA5 ? 7.5 : 8.5,
      cellPadding: isA5 ? 2.5 : 3.5,
      lineWidth: 0,
    },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: margin, right: margin },
    theme: "grid",
    didDrawPage: (data) => {
      // Footer di setiap halaman
      const pageCount = doc.internal.getNumberOfPages();
      const pageNum = data.pageNumber;
      doc.setFontSize(7);
      doc.setTextColor(GRAY_MUTED.r, GRAY_MUTED.g, GRAY_MUTED.b);
      doc.setFont("helvetica", "normal");
      const foundationFull = settings?.foundation_name || "";
      if (foundationFull) doc.text(foundationFull, margin, H - 6);
      doc.text(`Halaman ${pageNum} dari ${pageCount}`, W - margin, H - 6, { align: "right" });
      // Garis footer
      doc.setDrawColor(GRAY_LINE.r, GRAY_LINE.g, GRAY_LINE.b);
      doc.setLineWidth(0.2);
      doc.line(margin, H - 10, W - margin, H - 10);
    },
  });

  // === SIGNATURE FOOTER — hanya render yang terisi ===
  const roles = [
    { title: "Bendahara Sekolah", name: (settings?.signatory_bendahara || "").trim() },
    { title: "Manager Keuangan", name: (settings?.signatory_manager_keuangan || "").trim() },
    { title: "Direktur", name: (settings?.signatory_direktur || "").trim() },
    { title: "Ketua Yayasan", name: (settings?.signatory_ketua_yayasan || "").trim() },
  ].filter((r) => r.name);  // skip signatory kosong

  const finalY = doc.lastAutoTable?.finalY || tableStartY + 20;
  const spaceAvailable = H - finalY - 20;  // ruang tersisa sampai footer

  if (roles.length > 0 && spaceAvailable > 30) {
    const sigY = finalY + 12;
    const cityDate = `${settings?.city || "Bandung"}, ${new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}`;

    // Kanan: kota + tanggal
    doc.setFontSize(9);
    doc.setTextColor(GRAY_TEXT.r, GRAY_TEXT.g, GRAY_TEXT.b);
    doc.setFont("helvetica", "italic");
    doc.text(cityDate, W - margin, sigY, { align: "right" });

    // Grid signature dinamis
    const sigWidth = Math.min(W - margin * 2, roles.length * 65);
    const startX = W - margin - sigWidth;
    const step = sigWidth / roles.length;

    roles.forEach((r, i) => {
      const cx = startX + step * i + step / 2;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(GRAY_TEXT.r, GRAY_TEXT.g, GRAY_TEXT.b);
      doc.text("Hormat kami,", cx, sigY + 8, { align: "center" });

      // Nama (bold, hitam)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(20, 20, 20);
      doc.text(r.name, cx, sigY + 26, { align: "center" });

      // Garis tipis di bawah nama
      const nameWidth = doc.getTextWidth(r.name);
      doc.setDrawColor(GRAY_LINE.r, GRAY_LINE.g, GRAY_LINE.b);
      doc.setLineWidth(0.3);
      doc.line(cx - nameWidth / 2 - 2, sigY + 28, cx + nameWidth / 2 + 2, sigY + 28);

      // Jabatan
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(GRAY_MUTED.r, GRAY_MUTED.g, GRAY_MUTED.b);
      doc.text(r.title, cx, sigY + 32, { align: "center" });
    });
  }

  doc.save(filename || `laporan-${Date.now()}.pdf`);
}

export function formatIDRPlain(v) {
  const n = Number(v || 0);
  return "Rp " + n.toLocaleString("id-ID");
}
